import { useMemo, useState } from "react";
import { AutoField, FieldLabel } from "@puckeditor/core";
import { Image as ImageIcon, Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";

import ImagePicker from "@/components/pages/image-picker";
import type { UploadedImage } from "@/hooks/use-image-upload";

type PrimitiveFieldType = "text" | "textarea" | "number" | "select";
type SupportedFieldType = PrimitiveFieldType | "image" | "string-list";

type StructuredValue = Record<string, unknown>;

interface StructuredFieldOption {
  label: string;
  value: string;
}

interface StructuredFieldDefinition {
  key: string;
  label: string;
  type: SupportedFieldType;
  placeholder?: string;
  options?: StructuredFieldOption[];
}

interface StructuredArrayFieldConfig {
  label: string;
  itemLabel: string;
  addItemLabel?: string;
  itemFields: StructuredFieldDefinition[];
  maxItems?: number;
  getDefaultItem: () => StructuredValue;
  normalizeValue: (value: unknown) => StructuredValue[];
}

interface StringListFieldConfig {
  label: string;
  itemLabel: string;
  addItemLabel?: string;
  maxItems?: number;
  normalizeValue: (value: unknown) => string[];
  placeholder?: string;
}

interface PuckCustomFieldProps<TField> {
  field: TField;
  onChange: (value: unknown, uiState?: Record<string, unknown>) => void;
  value: unknown;
  readOnly?: boolean;
}

export interface TestimonialItem extends StructuredValue {
  quote: string;
  author: string;
  role: string;
}

export interface FaqItem extends StructuredValue {
  question: string;
  answer: string;
}

export interface GalleryItem extends StructuredValue {
  src: string;
  alt: string;
  caption: string;
}

export interface PricingTierItem extends StructuredValue {
  name: string;
  price: string;
  features: string[];
  ctaText: string;
  isPopular: boolean;
}

export interface FeatureItem extends StructuredValue {
  title: string;
  description: string;
}

export interface StatItem extends StructuredValue {
  number: string;
  label: string;
}

interface ImageFieldConfig {
  label: string;
  helperText?: string;
}

const DEFAULT_MAX_ITEMS = 50;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function readStringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item.trim();
        if (isRecord(item)) return readString(item.value ?? item.feature ?? item.label ?? item.text);
        return "";
      })
      .filter(Boolean);
  }

  if (typeof value !== "string") {
    return [];
  }

  return value
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);
}

function parsePipeRows<T>(value: string, mapRow: (parts: string[]) => T | null): T[] {
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => mapRow(line.split("|").map((part) => part.trim())))
    .filter((item): item is T => item !== null);
}

export function normalizeTestimonialItems(value: unknown): TestimonialItem[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (!isRecord(item)) return null;
        return {
          quote: readString(item.quote),
          author: readString(item.author),
          role: readString(item.role),
        };
      })
      .filter((item): item is TestimonialItem => item !== null)
      .filter((item) => item.quote || item.author || item.role);
  }

  if (typeof value !== "string") {
    return [];
  }

  return parsePipeRows(value, ([quote, author, role]) => ({
    quote: quote ?? "",
    author: author ?? "",
    role: role ?? "",
  }));
}

export function normalizeFaqItems(value: unknown): FaqItem[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (!isRecord(item)) return null;
        return {
          question: readString(item.question),
          answer: readString(item.answer),
        };
      })
      .filter((item): item is FaqItem => item !== null)
      .filter((item) => item.question || item.answer);
  }

  if (typeof value !== "string") {
    return [];
  }

  return parsePipeRows(value, ([question, answer]) => ({
    question: question ?? "",
    answer: answer ?? "",
  }));
}

export function normalizeGalleryItems(value: unknown): GalleryItem[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") {
          const src = readString(item);
          return src ? { src, alt: "", caption: "" } : null;
        }

        if (!isRecord(item)) return null;

        return {
          src: readString(item.src ?? item.url),
          alt: readString(item.alt),
          caption: readString(item.caption),
        };
      })
      .filter((item): item is GalleryItem => item !== null)
      .filter((item) => item.src);
  }

  if (typeof value !== "string") {
    return [];
  }

  return parsePipeRows(value, ([src, alt, caption]) => {
    if (!src) return null;
    return {
      src,
      alt: alt ?? "",
      caption: caption ?? "",
    };
  });
}

export function normalizeFeatureItems(value: unknown): FeatureItem[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (!isRecord(item)) return null;
        return {
          title: readString(item.title),
          description: readString(item.description ?? item.desc),
        };
      })
      .filter((item): item is FeatureItem => item !== null)
      .filter((item) => item.title || item.description);
  }

  if (typeof value !== "string") {
    return [];
  }

  return parsePipeRows(value, ([title, description]) => ({
    title: title ?? "",
    description: description ?? "",
  }));
}

export function normalizeStatItems(value: unknown): StatItem[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (!isRecord(item)) return null;
        return {
          number: readString(item.number ?? item.value),
          label: readString(item.label),
        };
      })
      .filter((item): item is StatItem => item !== null)
      .filter((item) => item.number || item.label);
  }

  if (typeof value !== "string") {
    return [];
  }

  return parsePipeRows(value, ([number, label]) => ({
    number: number ?? "",
    label: label ?? "",
  }));
}

export function normalizePricingFeatures(value: unknown): string[] {
  return readStringList(value);
}

export function normalizePricingTierItems(value: unknown): PricingTierItem[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (!isRecord(item)) return null;
        return {
          name: readString(item.name),
          price: readString(item.price),
          features: normalizePricingFeatures(item.features),
          ctaText: readString(item.ctaText),
          isPopular: item.isPopular === true || readString(item.isPopular).toLowerCase() === "true",
        };
      })
      .filter((item): item is PricingTierItem => item !== null)
      .filter((item) => item.name || item.price || item.features.length > 0 || item.ctaText);
  }

  return [];
}

export function createStructuredArrayField(config: StructuredArrayFieldConfig) {
  return {
    type: "custom" as const,
    ...config,
    render: StructuredArrayFieldEditor,
  };
}

export function createStringListField(config: StringListFieldConfig) {
  return {
    type: "custom" as const,
    ...config,
    render: StringListFieldEditor,
  };
}

export function createImageField(config: ImageFieldConfig) {
  return {
    type: "custom" as const,
    ...config,
    render: ImageFieldEditor,
  };
}

function StructuredArrayFieldEditor({
  field,
  onChange,
  readOnly,
  value,
}: PuckCustomFieldProps<StructuredArrayFieldConfig>) {
  const items = useMemo(() => field.normalizeValue(value), [field, value]);
  const maxItems = field.maxItems ?? DEFAULT_MAX_ITEMS;
  const canAdd = items.length < maxItems && !readOnly;

  const updateItem = (index: number, nextItem: StructuredValue) => {
    const nextItems = items.map((item, itemIndex) => (itemIndex === index ? nextItem : item));
    onChange(nextItems);
  };

  const moveItem = (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= items.length) return;
    const nextItems = [...items];
    const current = nextItems[index];
    const target = nextItems[targetIndex];
    if (!current || !target) return;
    nextItems[index] = target;
    nextItems[targetIndex] = current;
    onChange(nextItems);
  };

  return (
    <FieldLabel label={field.label}>
      <div className="space-y-3">
        {items.map((item, index) => (
          <div key={index} className="rounded-lg border border-border bg-background/60 p-3">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-xs font-medium text-foreground">
                {field.itemLabel} {index + 1}
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="rounded-md p-1 text-muted-foreground hover:bg-accent disabled:opacity-40"
                  disabled={readOnly || index === 0}
                  onClick={() => moveItem(index, -1)}
                  aria-label={`Move ${field.itemLabel.toLowerCase()} up`}
                >
                  <ChevronUp className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  className="rounded-md p-1 text-muted-foreground hover:bg-accent disabled:opacity-40"
                  disabled={readOnly || index === items.length - 1}
                  onClick={() => moveItem(index, 1)}
                  aria-label={`Move ${field.itemLabel.toLowerCase()} down`}
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  className="rounded-md p-1 text-muted-foreground hover:bg-accent disabled:opacity-40"
                  disabled={readOnly}
                  onClick={() => onChange(items.filter((_, itemIndex) => itemIndex !== index))}
                  aria-label={`Remove ${field.itemLabel.toLowerCase()}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            <div className="space-y-3">
              {field.itemFields.map((itemField) => (
                <StructuredItemFieldEditor
                  key={itemField.key}
                  definition={itemField}
                  readOnly={readOnly}
                  value={item[itemField.key]}
                  onChange={(nextValue) => updateItem(index, { ...item, [itemField.key]: nextValue })}
                />
              ))}
            </div>
          </div>
        ))}

        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-md border border-dashed border-border px-3 py-2 text-sm text-muted-foreground hover:bg-accent disabled:opacity-40"
          disabled={!canAdd}
          onClick={() => onChange([...items, field.getDefaultItem()])}
        >
          <Plus className="h-4 w-4" />
          {field.addItemLabel ?? `Add ${field.itemLabel.toLowerCase()}`}
        </button>
      </div>
    </FieldLabel>
  );
}

function StructuredItemFieldEditor({
  definition,
  onChange,
  readOnly,
  value,
}: {
  definition: StructuredFieldDefinition;
  value: unknown;
  onChange: (value: unknown) => void;
  readOnly?: boolean;
}) {
  if (definition.type === "image") {
    return (
      <InlineImageField
        label={definition.label}
        helperText={definition.placeholder}
        onChange={onChange}
        readOnly={readOnly}
        value={readString(value)}
      />
    );
  }

  if (definition.type === "string-list") {
    return (
      <StringListFieldEditor
        field={{
          label: definition.label,
          itemLabel: definition.label,
          addItemLabel: `Add ${definition.label.toLowerCase().replace(/s$/, "")}`,
          normalizeValue: readStringList,
          placeholder: definition.placeholder,
        }}
        onChange={onChange}
        readOnly={readOnly}
        value={value}
      />
    );
  }

  return (
    <AutoField
      field={{
        type: definition.type,
        label: definition.label,
        placeholder: definition.placeholder,
        options: definition.options,
      }}
      onChange={onChange}
      readOnly={readOnly}
      value={typeof value === "number" ? value : readString(value)}
    />
  );
}

function StringListFieldEditor({
  field,
  onChange,
  readOnly,
  value,
}: PuckCustomFieldProps<StringListFieldConfig>) {
  const items = useMemo(() => field.normalizeValue(value), [field, value]);
  const maxItems = field.maxItems ?? DEFAULT_MAX_ITEMS;
  const canAdd = items.length < maxItems && !readOnly;

  const updateItem = (index: number, nextValue: string) => {
    const nextItems = items.map((item, itemIndex) => (itemIndex === index ? nextValue : item));
    onChange(nextItems.filter((item) => item.trim().length > 0 || nextItems.length === 1));
  };

  const moveItem = (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= items.length) return;
    const nextItems = [...items];
    const current = nextItems[index];
    const target = nextItems[targetIndex];
    if (current === undefined || target === undefined) return;
    nextItems[index] = target;
    nextItems[targetIndex] = current;
    onChange(nextItems);
  };

  return (
    <FieldLabel label={field.label}>
      <div className="space-y-3">
        {items.map((item, index) => (
          <div key={index} className="rounded-lg border border-border bg-background/60 p-3">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-xs font-medium text-foreground">
                {field.itemLabel} {index + 1}
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="rounded-md p-1 text-muted-foreground hover:bg-accent disabled:opacity-40"
                  disabled={readOnly || index === 0}
                  onClick={() => moveItem(index, -1)}
                >
                  <ChevronUp className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  className="rounded-md p-1 text-muted-foreground hover:bg-accent disabled:opacity-40"
                  disabled={readOnly || index === items.length - 1}
                  onClick={() => moveItem(index, 1)}
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  className="rounded-md p-1 text-muted-foreground hover:bg-accent disabled:opacity-40"
                  disabled={readOnly}
                  onClick={() => onChange(items.filter((_, itemIndex) => itemIndex !== index))}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            <AutoField
              field={{
                type: "text",
                label: field.itemLabel,
                placeholder: field.placeholder,
              }}
              onChange={(nextValue) => updateItem(index, readString(nextValue))}
              readOnly={readOnly}
              value={item}
            />
          </div>
        ))}

        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-md border border-dashed border-border px-3 py-2 text-sm text-muted-foreground hover:bg-accent disabled:opacity-40"
          disabled={!canAdd}
          onClick={() => onChange([...items, ""])}
        >
          <Plus className="h-4 w-4" />
          {field.addItemLabel ?? `Add ${field.itemLabel.toLowerCase()}`}
        </button>
      </div>
    </FieldLabel>
  );
}

function ImageFieldEditor({
  field,
  onChange,
  readOnly,
  value,
}: PuckCustomFieldProps<ImageFieldConfig>) {
  return (
    <FieldLabel label={field.label}>
      <InlineImageField
        helperText={field.helperText}
        onChange={onChange}
        readOnly={readOnly}
        value={readString(value)}
      />
    </FieldLabel>
  );
}

function InlineImageField({
  helperText,
  label,
  onChange,
  readOnly,
  value,
}: {
  label?: string;
  helperText?: string;
  onChange: (value: unknown) => void;
  readOnly?: boolean;
  value: string;
}) {
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const handleSelect = (image: UploadedImage) => {
    onChange(image.url);
    setIsPickerOpen(false);
  };

  return (
    <div className="space-y-3">
      {label ? <div className="text-xs font-medium text-foreground">{label}</div> : null}
      <div className="overflow-hidden rounded-lg border border-border bg-background">
        <div className="flex min-h-32 items-center justify-center bg-muted/40">
          {value ? (
            <img src={value} alt="" className="max-h-40 w-full object-cover" />
          ) : (
            <div className="flex flex-col items-center gap-2 px-4 py-6 text-center text-xs text-muted-foreground">
              <ImageIcon className="h-6 w-6" />
              <span>{helperText ?? "Choose from your media library or upload a new image."}</span>
            </div>
          )}
        </div>
        <div className="space-y-3 border-t border-border p-3">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="rounded-md bg-[var(--color-primary)] px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
              disabled={readOnly}
              onClick={() => setIsPickerOpen(true)}
            >
              {value ? "Replace image" : "Choose image"}
            </button>
            {value ? (
              <button
                type="button"
                className="rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground disabled:opacity-40"
                disabled={readOnly}
                onClick={() => onChange("")}
              >
                Remove
              </button>
            ) : null}
          </div>
          <AutoField
            field={{
              type: "text",
              label: "Image URL",
              placeholder: "Paste a URL only if the image already lives elsewhere",
            }}
            onChange={onChange}
            readOnly={readOnly}
            value={value}
          />
        </div>
      </div>

      {isPickerOpen ? (
        <ImagePicker
          onClose={() => setIsPickerOpen(false)}
          onSelect={handleSelect}
        />
      ) : null}
    </div>
  );
}
